import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Video } from 'lucide-react';
import { CLIP_FORMATS, CLIP_FORMAT_LABEL_KEYS, type ClipFormat } from '@/lib/projects/clipFormats';

interface ClipFormatSelectorProps {
  value: string;
  onChange: (format: ClipFormat) => void;
}

/** Card picker for a project/library clip length, shared by the duplicate and copy dialogs. */
export default function ClipFormatSelector({ value, onChange }: ClipFormatSelectorProps) {
  const { t } = useTranslation();
  const labelId = useId();
  return (
    <div className="space-y-3">
      <label id={labelId} className="text-sm font-semibold flex items-center gap-2 text-foreground">
        <Video className="w-4 h-4 text-primary" />
        {t('uploadModal.videoFormat')}
      </label>
      <div role="radiogroup" aria-labelledby={labelId} className="grid grid-cols-3 gap-3">
        {CLIP_FORMATS.map((format) => {
          const selected = value === format;
          return (
            <button
              key={format}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(format)}
              className={`p-3 rounded-xl border-2 text-left transition-all duration-300 hover:scale-105 ${
                selected
                  ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/5 shadow-glow'
                  : 'border-border hover:border-primary/40 bg-background/50'
              }`}
            >
              <span className="block font-semibold text-xs text-foreground">{t(CLIP_FORMAT_LABEL_KEYS[format])}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
